import React from "react";
import { action, observable, computed } from "mobx";
import { observer } from "mobx-react";

import {
  Checkbox,
  createStyles,
  Divider,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  withStyles,
  TextField,
  InputAdornment
} from "@material-ui/core";
import {
  ClearRounded,
  Edit,
  PersonAdd,
  PlaylistAddRounded,
  StarRounded,
  Search
} from "@material-ui/icons";
import { store } from "@store";
import { Classes, ThemeType } from "@theme";
import locale from "@bonliva-crm/locale";
import { OkCancelDialogOptions } from "@containers/ConfirmDialog";
import {
  CallListType,
  NodeCallList_Add,
  NodeCallList_GetById,
  NodeCallList_LeaveList,
  NodeCallList_Remove,
  NodeRefFullInput,
  UserStore_CallListFragment,
  useNodeCallList_GetListsLazyQuery,
  SimpleCustomerCard_Get
} from "@graphql";
import { Body } from "@components";
import Highlighter from "@components/Highlighter";

import CallListEditorDialog from "../CallListEditor/CallListEditorDialog";

type Props = {
  classes?: Classes<typeof styles>;
  node: NodeRefFullInput;
  callLists?: UserStore_CallListFragment[];
  contactLists?: UserStore_CallListFragment[];
  refetchLists?: () => Promise<any>;
};

@observer
export class NodeCallList extends React.Component<Props> {
  @observable editingCallListId: string | null = null;
  @observable loadingLists: string[] = [];
  @observable type: CallListType = null;
  @observable private _name = "";

  isEditorOpen = () => this.editingCallListId !== null;

  getCallLists = (name: string | null) => {
    const source = (this.props.callLists ?? []).slice();
    const regex = new RegExp(name ?? "", "i");

    const callList = source
      .filter((x) => x.type === this.props.node.NodeType)
      .filter((x) => !x.deleted)
      .filter((x) => x.members.any((y) => y.PersonId === store().user.id))
      .sortBy((x) => x.name.toLocaleLowerCase())
      .filter((x) => x.name.match(regex));

    return callList;
  };

  getContactLists = (name: string | null) => {
    const source = (this.props.contactLists ?? []).slice();
    const regex = new RegExp(name ?? "", "i");

    const contactList = source
      .filter((x) => x.type === this.props.node.NodeType)
      .filter((x) => !x.deleted)
      .filter((x) => x.members.any((y) => y.PersonId === store().user.id))
      .sortBy((x) => x.name.toLocaleLowerCase())
      .filter((x) => x.name.match(regex));

    return contactList;
  };

  openUserSelector = (listId: string) => {
    NodeCallList_GetById.func({ id: listId }, { fetchPolicy: "network-only" })
      .then((results) => {
        const selectedIds = results.data.callListById.callList.members.map((user) => {
          return user.PersonId;
        });
        return store().ui.userSelector.open({
          listId,
          selectedUsers: selectedIds,
          multi: true,
          includeActiveUser: false,
          onSelect: (user) => {
            store().ui.userSelector.updateSelectedUsers(user._id);
          }
        });
      })
      .catch((e) => {
        store().snackbars.error(`Cannot add item to call list\nReason: ${e}`);
      });

    store().ui.userSelector.open({
      onSelect: (user) => {
        store().ui.userSelector.updateSelectedUsers(user._id);
      },
      selectedUsers: [],
      multi: true
    });
  };

  @action openCallListEditor = (id: string | null, type: CallListType) => {
    this.editingCallListId = id;
    this.type = type;
  };

  @computed
  get name(): string {
    return this._name;
  }

  set name(value: string) {
    this._name = value;
  }

  @action handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.name = event.target.value;
  };

  @action closeCallListEditor = () => {
    this.editingCallListId = null;
    this.type = null;
  };

  handleCheck = (listId: string, _name?: string) => {
    const allLists = [
      ...(this.props.callLists ?? []),
      ...(this.props.contactLists ?? [])
    ];

    const list = allLists.find((x) => x._id === listId);
    const isAlreadyInList = !!list?.nodes.any((n) => n.NodeID === this.props.node.NodeID);

    return isAlreadyInList ? this.removeNodeFromList(listId) : this.addToCallList(listId);
  };

  addToCallList = (listId: string) => {
    this.loadingLists.push(listId);

    NodeCallList_Add.func(
      { id: listId, node: this.props.node },
      { refetchQueries: [SimpleCustomerCard_Get.OperationName] }
    )
      .then(() => {
        store().snackbars.success(`Lagt till`);
        return this.props.refetchLists?.();
      })
      .catch((e) => {
        store().snackbars.error(`Cannot add item to call list\nReason: ${e}`);
      })
      .finally(() => {
        this.loadingLists.remove(listId);
      });
  };

  removeNodeFromList = (listId: string) => {
    this.loadingLists.push(listId);

    NodeCallList_Remove.func(
      { id: listId, nodeId: this.props.node.NodeID },
      { refetchQueries: [SimpleCustomerCard_Get.OperationName] }
    )
      .then(() => {
        store().snackbars.success(`Tagit bort`);
        return this.props.refetchLists?.();
      })
      .catch((e) => {
        store().snackbars.error(`Cannot remove item from the call list\nReason: ${e}`);
      })
      .finally(() => {
        this.loadingLists.remove(listId);
      });
  };

  confirmDeleteList = (listId: string) => {
    const allLists = [
      ...(this.props.callLists ?? []),
      ...(this.props.contactLists ?? [])
    ];

    const target = allLists.find((x) => x._id === listId);
    const memberCount = target?.members?.length ?? 0;
    const listIsEmpty = memberCount === 1;

    store()
      .ui.confirmDialog.confirm(
        new OkCancelDialogOptions(
          listIsEmpty
            ? locale.CallListRemoveWithoutMembers
            : locale.CallListRemoveWithMembers
        )
      )
      .then((confirmed) => {
        if (confirmed !== "ok") {
          return;
        }

        this.leaveList(listId);
      });
  };

  leaveList = (listId: string) => {
    this.loadingLists.push(listId);

    NodeCallList_LeaveList.func(
      { id: listId },
      { refetchQueries: [SimpleCustomerCard_Get.OperationName] }
    )
      .then(() => {
        return this.props.refetchLists?.();
      })
      .catch((e) => {
        store().snackbars.error(`Cannot delete call list\nReason: ${e}`);
      })
      .finally(() => {
        this.loadingLists.remove(listId);
      });
  };

  render() {
    const { classes, node } = this.props;

    const callLists = this.getCallLists(this.name);
    const contactLists = this.getContactLists(this.name);

    const showDivider = Boolean(callLists.length || contactLists.length);

    return (
      <>
        {this.renderSearchBar()}
        <List disablePadding data-test="NodeCallList">
          {!!callLists.length &&
            this.renderLists(CallListType.CallList, locale.CallLists, callLists)}
          {!!callLists.length && <Divider />}
          {!!contactLists.length &&
            this.renderLists(CallListType.ContactList, locale.ContactLists, contactLists)}
          {showDivider && <Divider />}

          <ListItem
            className={classes.add}
            button
            onClick={() => this.openCallListEditor("", CallListType.CallList)} // "" for an empty id a.k.a a new item
          >
            <PlaylistAddRounded />
            <ListItemText style={{ paddingLeft: 8 }}>
              <Body>{locale.AddToNewCallList}</Body>
            </ListItemText>
          </ListItem>

          <ListItem
            className={classes.add}
            button
            onClick={() => this.openCallListEditor("", CallListType.ContactList)}
          >
            <PlaylistAddRounded />
            <ListItemText style={{ paddingLeft: 8 }}>
              <Body>{locale.AddToNewContactList}</Body>
            </ListItemText>
          </ListItem>
        </List>

        <CallListEditorDialog
          open={this.isEditorOpen()}
          id={this.editingCallListId}
          node={node}
          type={this.props.node.NodeType}
          onClose={this.closeCallListEditor}
          listType={this.type}
          onAdded={() => this.props.refetchLists?.()}
        />
      </>
    );
  }

  private renderSearchBar = () => {
    return (
      <TextField
        style={{ padding: 16 }}
        placeholder={locale.Search}
        onChange={this.handleChange}
        value={this.name}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search color="action" fontSize="small" />
            </InputAdornment>
          )
        }}
      />
    );
  };

  private renderLists = (
    type: CallListType,
    label: string,
    lists: UserStore_CallListFragment[]
  ) => {
    const { classes } = this.props;

    return (
      <>
        <ListItem>
          <StarRounded color="primary" />
          <ListItemText style={{ paddingLeft: 8 }}>
            <Body>{label}</Body>
          </ListItemText>
        </ListItem>

        {lists.map((list) => (
          <ListItem
            className={classes.listItem}
            key={list._id}
            button
            onClick={() => this.handleCheck(list._id, list.name)}
            disabled={this.loadingLists.includes(list._id)}
          >
            <Checkbox
              className={classes.checkbox}
              checked={list.nodes.any((x) => x.NodeID === this.props.node.NodeID)}
              disableRipple
            />

            <ListItemText style={{ paddingLeft: 8 }}>
              <Highlighter search={this.name}>{list.name}</Highlighter>
            </ListItemText>

            <ListItemSecondaryAction className={classes.actions}>
              <PersonAdd onClick={() => this.openUserSelector(list._id)} />
              <Edit onClick={() => this.openCallListEditor(list._id, type)} />
              <ClearRounded onClick={() => this.confirmDeleteList(list._id)} />
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </>
    );
  };
}

const styles = (theme: ThemeType) =>
  createStyles({
    listItem: {
      paddingTop: 0,
      paddingBottom: 0,
      paddingRight: 100
    },

    checkbox: {
      padding: 0,
      paddingTop: 4,
      paddingBottom: 4
    },

    add: {
      "& svg": {
        color: theme.palette.grey["600"]
      }
    },

    actions: {
      fontSize: 12,

      "& svg": {
        color: theme.palette.grey["400"],
        cursor: "pointer",
        transition: theme.transitions.create("color"),

        "&:hover": {
          color: theme.palette.grey["800"]
        }
      },

      "& > *:not(:last-child)": {
        marginRight: theme.spacing(1 / 2)
      }
    }
  });

const ObservedStyledNodeCallList = withStyles(styles)(NodeCallList);

export default function NodeCallListWithData(
  props: Omit<Props, "callLists" | "contactLists" | "refetchLists">
) {
  const [fetchLists, { data, loading, called, refetch }] =
    useNodeCallList_GetListsLazyQuery({ fetchPolicy: "network-only" });

  React.useEffect(() => {
    if (!called && !loading) {
      fetchLists();
    }
  }, [called, loading, fetchLists]);

  const refetchLists = React.useCallback(() => {
    return called ? refetch() : fetchLists();
  }, [called, refetch, fetchLists]);

  const callLists = (data?.me?.CallLists ?? []) as UserStore_CallListFragment[];
  const contactLists = (data?.me?.ContactLists ?? []) as UserStore_CallListFragment[];

  return (
    <ObservedStyledNodeCallList
      {...(props as Props)}
      callLists={callLists}
      contactLists={contactLists}
      refetchLists={refetchLists} // ⬅️ pass down
    />
  );
}
